import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Quote, Linkedin, PenLine, Loader2 } from "lucide-react";

import { RecommendationForm } from "./RecommendationForm";
import { LocalizedLink } from "./LocalizedLink";
import { useLanguage } from "../i18n/LanguageProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Avatar = ({ name, avatarUrl }) => {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="w-10 h-10 rounded-full object-cover border border-white/5 shrink-0"
        onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
      />
    );
  }
  return null;
};

const AvatarFallback = ({ name, hidden }) => (
  <div
    className="w-10 h-10 rounded-full bg-zinc-800 border border-white/5 items-center justify-center shrink-0"
    style={{ display: hidden ? "none" : "flex" }}
  >
    <span className="text-xs font-medium text-zinc-300">{getInitials(name)}</span>
  </div>
);

// Parse role_company into role and company
const parseRoleCompany = (roleCompany) => {
  if (!roleCompany) return { role: "", company: "" };
  const parts = roleCompany.split(/[,@]/).map(p => p.trim());
  if (parts.length >= 2) {
    return { role: parts[0], company: parts[1] };
  }
  return { role: roleCompany, company: "" };
};

const TestimonialCard = ({ testimonial, index }) => {
  const { role, company } = parseRoleCompany(testimonial.role_company);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`testimonial-card-${testimonial.id}`}
      className="group relative bg-[#0F0F10] border border-white/5 rounded-2xl p-8 flex flex-col card-shine border-glow"
    >
      <div className="mb-6">
        <Quote size={20} className="text-zinc-700" strokeWidth={1.5} />
      </div>

      <blockquote className="text-[15px] text-zinc-400 leading-[1.8] mb-8 flex-1">
        "{testimonial.message || testimonial.content}"
      </blockquote>

      <div className="flex items-center justify-between gap-4 pt-6 border-t border-white/5">
        <div className="flex items-center gap-3">
          <div className="relative shrink-0" data-testid={`avatar-${testimonial.id}`}>
            <Avatar name={testimonial.name} avatarUrl={testimonial.avatar_url || testimonial.avatarUrl} />
            <AvatarFallback name={testimonial.name} hidden={!!(testimonial.avatar_url || testimonial.avatarUrl)} />
          </div>
          <div>
            <p className="text-sm font-medium text-white">{testimonial.name}</p>
            <p className="text-xs text-zinc-500">
              {role}{company ? `, ${company}` : ""}
            </p>
          </div>
        </div>
        {(testimonial.linkedin_url || testimonial.linkedinUrl) && (
          <a
            href={testimonial.linkedin_url || testimonial.linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`testimonial-linkedin-${testimonial.id}`}
            className="p-2 rounded-lg text-zinc-600 hover:text-zinc-300 hover:bg-white/5 transition-colors duration-200"
          >
            <Linkedin size={14} strokeWidth={1.5} />
          </a>
        )}
      </div>
    </motion.div>
  );
};

export { TestimonialCard, getInitials };

export const Testimonials = () => {
  const { t } = useLanguage();
  const [formOpen, setFormOpen] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/testimonials`);
        if (res.ok) {
          const data = await res.json();
          setTestimonials(data.slice(0, 3)); // Only show first 3 on homepage
        }
      } catch (err) {
        console.error("Failed to fetch testimonials:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-sm tracking-widest uppercase text-zinc-500 font-medium mb-4"
        >
          {t("testimonials.label")}
        </motion.p>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-semibold tracking-tight text-white/90"
          >
            {t("testimonials.sectionTitle")}
          </motion.h2>
          <motion.button
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.15 }}
            onClick={() => setFormOpen(true)}
            data-testid="leave-recommendation-btn-home"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white px-4 py-2 rounded-full border border-zinc-800 hover:border-zinc-600 transition-colors duration-200 shrink-0 w-fit"
          >
            <PenLine size={14} strokeWidth={1.5} />
            {t("testimonials.leaveOne")}
          </motion.button>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-base text-zinc-500 max-w-xl mb-16 leading-relaxed"
        >
          {t("testimonials.intro")}
        </motion.p>

        {/* Featured grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-zinc-600" />
          </div>
        ) : testimonials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {testimonials.map((t, i) => (
              <TestimonialCard key={t.id} testimonial={t} index={i} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-zinc-600">
            <p className="mb-4">{t("testimonials.empty")}</p>
            <button
              onClick={() => setFormOpen(true)}
              className="text-zinc-400 hover:text-white underline underline-offset-4"
            >
              {t("testimonials.beFirst")}
            </button>
          </div>
        )}

        {/* View All */}
        {testimonials.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-10 flex justify-center"
          >
            <LocalizedLink
              to="/testimonials"
              data-testid="view-all-testimonials-btn"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800 px-6 py-3 rounded-full font-medium text-sm transition-colors duration-200"
            >
              {t("testimonials.viewAll")}
              <ArrowRight size={16} strokeWidth={1.5} />
            </LocalizedLink>
          </motion.div>
        )}
      </div>

      <RecommendationForm isOpen={formOpen} onClose={() => setFormOpen(false)} />
    </section>
  );
};
